import { useEffect, useState } from "react";

import * as yup from "yup";
import Link from "next/link";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { Button, Card, Col, Container, Form, Row, Spinner } from "reactstrap";
import Image from "next/image";
import { useRouter } from "next/router";

import classes from "@/styles/auth/login.module.css";
import { routes } from "@/utils/routes";
import InputGroup from "../common/input-group";
import useAlertBar from "@/hooks/useAlertBar";
import { loginService } from "@/services/authService";
import Hero from "../generic/hero";
import About from "../generic/about";
import { useIsMobile } from "@/hooks/useIsMobile";
import { shipvistaLargeLogo } from "@/utils/images";
import BlogCarousel from "../generic/blog-carousel";
import { getUser } from "@/services/userService";
import { postLoginHandoff } from "./postLoginHandoff";

type Inputs = {
  email: string;
  password: string;
};

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email Address is required"),
  password: yup.string().required("Password is required"),
});

export default function Login() {
  const { openAlertBar } = useAlertBar();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(validationSchema),
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const onSubmit: SubmitHandler<Inputs> = async (formData) => {
    setLoading(true);
    try {
      const requestBody = {
        user_id: formData.email ? formData.email.toLowerCase() : formData.email,
        password: formData.password,
      };

      const { data } = await loginService(requestBody);

      if (data.requires_mfa) {
        // MFA required — no session tokens issued yet
        sessionStorage.setItem(
          "sv_mfa_challenge",
          JSON.stringify({
            mfaToken: data.mfa_token,
            userId: requestBody.user_id,
          }),
        );
        router.push("/mfa-challenge");
        return;
      }

      if (data.status) {
        const { tokenString, expireAt } = data.access_token;
        const refreshTokenString = data.refresh_token?.tokenString;
        const refreshExpireAt = data.refresh_token?.expireAt;

        if (data.requires_password_change) {
          // Session tokens issued but user must change password first
          sessionStorage.setItem(
            "sv_pwd_change",
            JSON.stringify({
              accessTokenString: tokenString,
              accessExpireAt: expireAt,
              refreshTokenString,
              refreshExpireAt,
            }),
          );
          router.push("/change-password-required");
          return;
        }

        window.localStorage.setItem("accessToken", tokenString);
        window.localStorage.setItem("expireAt", expireAt);

        const user = await getUser(tokenString);

        if (user.data.is_beta_user) {
          router.push(routes.dashboardSelect);
          return;
        }

        // Plain login — redirect to dashboard
        postLoginHandoff({
          accessTokenString: tokenString,
          accessExpireAt: expireAt,
          refreshTokenString,
          refreshExpireAt,
        });
      } else {
        openAlertBar(data.message, false);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      openAlertBar(
        "Unexpected error occurred while trying to log you in. Please try again ",
        false,
      );
      console.log(error);
    }
  };

  const redirectToHome = () => {
    router.push(routes.home);
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <>
      <Container className="my-5">
        <Card className={`border-0 ${classes.mainCard} shadow`}>
          <Row>
            {useIsMobile() ? null : (
              <Col
                className={
                  classes.col1 + " d-flex align-items-center flex-wrap rounded"
                }
                xs={12}
                lg={7}
              >
                <div className="mx-auto">
                  <div className="text-center ">
                    <Image
                      src={shipvistaLargeLogo}
                      alt="Shipvista Logo"
                      className="img-fluid"
                      width={320}
                      height={100}
                    />
                  </div>
                  <div className="mb-5">
                    <BlogCarousel />
                  </div>
                </div>
              </Col>
            )}
            <Col xs={12} lg={5}>
              <div className="mx-auto p-3">
                <Form
                  onSubmit={handleSubmit(onSubmit)}
                  className="my-4 needs-validation"
                >
                  <h2 className="text-center mb-4">Login</h2>
                  <InputGroup
                    required
                    label="Email Address"
                    name="email"
                    register={register}
                    error={errors.email?.message}
                  />
                  <InputGroup
                    required
                    label="Password"
                    type="password"
                    name="password"
                    register={register}
                    error={errors.password?.message}
                  />
                  <Row>
                    <Col sm={12}>
                      <Link href={routes.forgotPassword}>Forgot Password?</Link>
                    </Col>
                    <Col className="text-end">
                      <div className="">
                        Don&#39;t have an account{" "}
                        <Link href={routes.register}>Sign Up Now?</Link>
                      </div>
                    </Col>
                  </Row>

                  <Button
                    type="submit"
                    disabled={loading}
                    className={`w-100 mt-2 ${classes.submitBtn}`}
                  >
                    LOGIN
                    {loading && <Spinner size="sm" className="ms-3" />}
                  </Button>
                </Form>
              </div>
            </Col>
          </Row>
        </Card>
      </Container>
      <div className={classes.overLay} onClick={redirectToHome}></div>
      <div className={classes.container} onClick={redirectToHome}>
        <About />
      </div>
    </>
  );
}
